import Button from '@rio-cloud/rio-uikit/Button';
import Collapse from '@rio-cloud/rio-uikit/Collapse';
import type { AssetType } from '@rio-cloud/rio-uikit/components/assetTree/TreeSummary';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';
import ToggleButton from '@rio-cloud/rio-uikit/ToggleButton';
import { FormattedMessage } from 'react-intl';

import type { RentalAssetData } from '../../../../domain/model/rental-asset.types';
import VehicleGrid from '../../../dialogs/VehicleGrid';
import ScreenshotDialog from '../1-1-0/ScreenshotDialog';

const vatId = 'DE123456789';
const vatMatchedAccountName = 'My Partner GmbH';

const selectedRental: RentalAssetData[] = [
    {
        asset: {
            assetId: 'asset-123',
            vehicleName: 'Vehicle 123',
            type: 'Truck' as AssetType,
            status: 'active',
            licensePlate: 'ABC123',
            licensePlateCountryCode: 'DE',
            vin: '1111B5GB0BA714579',
        },
        rental: null,
        renterAccountDisplayName: vatMatchedAccountName,
        hasRentalPartnershipWithRenter: false,
        devices: [],
    },
];

export const VATBasedRentalDialogScreenshot = () => (
    <ScreenshotDialog
        title={<FormattedMessage id='intl-msg:rental-web.dialog.start-rental.title' />}
        buttons={
            <Button bsStyle='primary' onClick={() => {}} disabled={false} iconName='rioglyph-ok-dashed'>
                <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.button.title' />
            </Button>
        }
    >
        <div className='display-flex gap-20 padding-x-25 padding-bottom-25'>
            <div className='width-250 display-flex flex-column items-center padding-top-25 padding-right-15'>
                <img src='https://cdn.rio.cloud/riosvg/custom/time-start.svg' alt='' />
            </div>
            <div>
                <p>
                    <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.content' />
                </p>

                <div className='margin-top-15'>
                    <div className='display-flex flex-column'>
                        <RadioButton
                            name='vatBasedRentalScreenshotRadioBoxes'
                            custom
                            className='padding-y-15'
                            onChange={() => {}}
                            checked
                        >
                            <div className='radio-text-wrapper display-flex'>
                                <span className='radio-text' />
                                <div className='margin-left-5 margin-right-5'>
                                    <div className='text-medium text-size-16'>
                                        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.title' />
                                    </div>
                                    <div className='text-color-dark'>
                                        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.description' />
                                    </div>
                                    <ToggleButton
                                        className='margin-top-5 margin-bottom-'
                                        active
                                        bsStyle='info'
                                        onClick={() => {}}
                                        variant='link'
                                    >
                                        <span className='rioglyph rioglyph-info-sign margin-right-5' />
                                        <FormattedMessage
                                            id='intl-msg:rental-web.dialog.start-rental.option.account-rental.description.vat.toggle'
                                            defaultMessage='How to start a rental with your partner’s VAT number'
                                        />
                                    </ToggleButton>
                                    <Collapse open>
                                        <div className='panel-wrapper'>
                                            <div className='panel panel-default'>
                                                <div className='panel-body text-color-dark'>
                                                    <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.account-rental.description.vat' />
                                                </div>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                            </div>
                        </RadioButton>

                        <RadioButton
                            name='vatBasedRentalScreenshotRadioBoxes'
                            custom
                            className='padding-y-15'
                            onChange={() => {}}
                            checked={false}
                        >
                            <div className='radio-text-wrapper display-flex'>
                                <span className='radio-text' />
                                <div className='margin-left-5 margin-right-5'>
                                    <div className='text-medium text-size-16'>
                                        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.option.external-rental.title' />
                                    </div>
                                    <div className='text-color-dark'>
                                        <FormattedMessage
                                            id='intl-msg:rental-web.dialog.start-rental.option.external-rental.description'
                                            values={{
                                                i: chunk => <i>{chunk}</i>,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </RadioButton>
                    </div>
                </div>

                <div className='margin-top-10'>
                    <div className='form-group'>
                        <FormLabel htmlFor='vat-based-rental-company'>
                            <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.title' />
                        </FormLabel>

                        <div className='form-group margin-bottom-0'>
                            <div className='form-control-feedback-wrapper'>
                                <div className='AutoSuggest dropdown open'>
                                    <div className='input-group width-100pct'>
                                        <span className='input-group-addon'>
                                            <span className='rioglyph rioglyph-building' aria-hidden='true' />
                                        </span>
                                        <div className='ClearableInput input-group'>
                                            <input
                                                id='vat-based-rental-company'
                                                placeholder='Enter partner name or VAT ID'
                                                className='form-control'
                                                autoComplete='off'
                                                type='text'
                                                tabIndex={0}
                                                value={vatId}
                                                readOnly
                                            />
                                            <span className='clearButton'>
                                                <span className='clearButtonIcon rioglyph rioglyph-remove-sign' />
                                            </span>
                                        </div>
                                    </div>
                                    <ul className='dropdown-menu'>
                                        <li role='presentation' data-item-index='0'>
                                            <a role='menuitem'>
                                                <span className='display-flex'>
                                                    <span className='text-color-primary rioglyph rioglyph-plus margin-right-5' />
                                                    <span className='display-flex align-items-center'>
                                                        <span>{vatMatchedAccountName}</span>
                                                        <small className='text-color-gray margin-left-5'>
                                                            VAT {vatId}
                                                        </small>
                                                    </span>
                                                </span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='margin-top-25'>
                        <FormLabel>
                            <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.vehicle-selection.label' />
                        </FormLabel>
                        <VehicleGrid rentalAssetData={selectedRental} />
                    </div>
                </div>
            </div>
        </div>
    </ScreenshotDialog>
);
